import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useInfiniteQuery, useQueries, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import DOMPurify from 'dompurify';
import {
  Mail, MessageSquare, Phone, Calendar as CalIcon,
  Globe, FileText, Send, Search, Facebook, MessageCircle, Instagram, Music2,
  Pin, PinOff, ArrowUpDown, Smile, CheckCheck, X, ArrowLeft, Target, Sparkles, Loader2, ClipboardList, Bot,
  Paperclip, Check, Download, Image as ImageIcon, LayoutTemplate, Clock, AlertCircle
} from 'lucide-react';
import api, { leadsAPI, notificationsAPI } from '../services/api';
import SettingsRequiredModal from '../components/SettingsRequiredModal';
import ConfirmationModal from '../components/ConfirmationModal';
import MetaLeadFormCard, { FORM_NOTE_SOURCES, looksLikeFormSubmission } from '../components/MetaLeadFormCard';
import AIReplyPanel from '../components/AIReplyPanel';
import AISuggestTasksPanel from '../components/AISuggestTasksPanel';
import VoipRecordingPlayer from '../components/VoipRecordingPlayer';
import useLeadClaim from '../hooks/useLeadClaim';
import BrowserCallButton from '../components/BrowserCallButton';
import { useAuth } from '../contexts/AuthContext';
import LeadAvatar from '../components/LeadAvatar';
import { smsPartCount, SMS_SEGMENT_LIMIT, smsOptOutNotice } from '../utils/sms';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';

// AI Auto-Reply stamps the outbound note with customFields.ai_autoreply
// (see server/services/aiAutoReplyService.js sendAndPersist). customFields
// may arrive as a Map (mongoose doc) or a plain object (JSON).
function isAiAutoReply(m) {
  const cf = m?.customFields;
  if (!cf) return false;
  const v = cf instanceof Map ? cf.get('ai_autoreply') : cf.ai_autoreply;
  return v === true || v === 'true';
}

// Audit trail the auto-reply service writes onto the note (see
// draftAndVerify in server/services/aiAutoReplyService.js): why the AI
// said what it said — or, for a safe acknowledgment, why its own answer
// was withheld.
const AI_FALLBACK_REASONS = {
  no_kb_match: 'No knowledge base entry covers this question',
  low_confidence: 'The AI was not confident enough in its answer',
  ungrounded_facts: 'The answer contained facts not found in the knowledge base or conversation',
  judge_rejected: 'The verification model flagged unsupported claims',
  judge_unavailable: 'The answer could not be verified',
  generation_failed: 'The AI was unavailable',
  empty_draft: 'The AI returned no answer',
};

function aiAuditOf(m) {
  const cf = m?.customFields instanceof Map ? Object.fromEntries(m.customFields) : (m?.customFields || {});
  const rawReason = String(cf.ai_fallback_reason || '');
  const [reasonKey, reasonDetail] = rawReason.split(':');
  return {
    fallback: cf.ai_fallback === true,
    reason: AI_FALLBACK_REASONS[reasonKey] || rawReason,
    reasonDetail: reasonKey === 'low_confidence' && reasonDetail && !Number.isNaN(Number(reasonDetail))
      ? `${Math.round(Number(reasonDetail) * 100)}%` : null,
    model: cf.ai_model || null,
    judgeModel: cf.ai_judge_model || null,
    confidence: typeof cf.ai_confidence === 'number' ? cf.ai_confidence : null,
    kbCited: Array.isArray(cf.ai_kb_cited) ? cf.ai_kb_cited : [],
    kbMatchedCount: typeof cf.ai_kb_matched_count === 'number' ? cf.ai_kb_matched_count : null,
    verified: cf.ai_checks?.judge === 'passed',
    ungrounded: Array.isArray(cf.ai_ungrounded) ? cf.ai_ungrounded : [],
    judgeIssues: Array.isArray(cf.ai_judge_issues) ? cf.ai_judge_issues : [],
    rejectedDraft: cf.ai_rejected_draft || '',
    emergencyKb: cf.ai_emergency_info ? (cf.ai_emergency_kb?.question || 'emergency entry') : null,
    followUp: cf.ai_followup ? { step: cf.ai_followup_step, total: cf.ai_followup_total } : null,
    reasoning: cf.ai_reasoning || '',
  };
}

const AiReplyAudit = ({ m }) => {
  const a = aiAuditOf(m);
  const modelName = (id) => String(id).split('/').pop();
  return (
    <div className="mt-1.5 rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-[11px] leading-relaxed text-violet-950 space-y-1.5 break-words">
      <div className="font-semibold">
        {a.followUp
          ? `AI follow-up ${a.followUp.step} of ${a.followUp.total}${a.fallback ? ' — safe check-in, the AI draft was withheld' : ''}`
          : a.fallback ? 'Safe acknowledgment — the AI answer was withheld' : 'Sent by AI Auto-Reply'}
      </div>
      {a.fallback && a.reason && (
        <div>Why: {a.reason}{a.reasonDetail ? ` (${a.reasonDetail})` : ''}.</div>
      )}
      {a.emergencyKb && (
        <div>The customer sounded urgent, so your emergency instructions were added from the knowledge base ("{a.emergencyKb}").</div>
      )}
      {!a.fallback && a.verified && (
        <div>Checks passed: knowledge base match, confidence, fact check, independent verification.</div>
      )}
      {(a.model || a.confidence != null) && (
        <div className="text-violet-800">
          {a.model && <>Model: {modelName(a.model)}</>}
          {a.confidence != null && <>{a.model ? ' · ' : ''}Confidence: {Math.round(a.confidence * 100)}%</>}
          {a.judgeModel && <> · Verified by: {modelName(a.judgeModel)}</>}
        </div>
      )}
      {a.kbCited.length > 0 && (
        <div>
          <div className="font-medium">Knowledge base sources</div>
          <ul className="list-disc pl-4">
            {a.kbCited.map((k, i) => <li key={k.id || i}>{k.question}</li>)}
          </ul>
        </div>
      )}
      {a.kbCited.length === 0 && a.kbMatchedCount > 0 && (
        <div>{a.kbMatchedCount} knowledge base {a.kbMatchedCount === 1 ? 'entry' : 'entries'} matched (none cited).</div>
      )}
      {a.ungrounded.length > 0 && (
        <div>
          <div className="font-medium">Unverified facts</div>
          <ul className="list-disc pl-4">{a.ungrounded.map((u, i) => <li key={i}>{u}</li>)}</ul>
        </div>
      )}
      {a.judgeIssues.length > 0 && (
        <div>
          <div className="font-medium">Verification issues</div>
          <ul className="list-disc pl-4">{a.judgeIssues.map((u, i) => <li key={i}>{u}</li>)}</ul>
        </div>
      )}
      {a.rejectedDraft && (
        <details>
          <summary className="cursor-pointer font-medium">Withheld AI draft</summary>
          <div className="mt-1 whitespace-pre-wrap text-violet-900">{a.rejectedDraft}</div>
        </details>
      )}
      {a.reasoning && (
        <details>
          <summary className="cursor-pointer font-medium">AI reasoning</summary>
          <div className="mt-1 whitespace-pre-wrap text-violet-900">{a.reasoning}</div>
        </details>
      )}
    </div>
  );
};

const CHANNEL_META = {
  all:         { label: 'All',        color: 'bg-gray-100 text-gray-800',       ring: 'ring-gray-400',   Icon: MessageCircle },
  email:       { label: 'Email',      color: 'bg-purple-100 text-purple-800',   ring: 'ring-purple-400', Icon: Mail },
  sms:         { label: 'SMS',        color: 'bg-teal-100 text-teal-800',       ring: 'ring-teal-400',   Icon: MessageSquare },
  phone:       { label: 'Phone',      color: 'bg-green-100 text-green-800',     ring: 'ring-green-400',  Icon: Phone },
  phone_agent: { label: 'Phone Agent',color: 'bg-cyan-100 text-cyan-800',       ring: 'ring-cyan-400',   Icon: Bot },
  webform:     { label: 'Web Form',   color: 'bg-blue-100 text-blue-800',       ring: 'ring-blue-400',   Icon: Globe },
  meta_ad:     { label: 'Meta Ad',    color: 'bg-indigo-100 text-indigo-800',   ring: 'ring-indigo-400', Icon: Facebook },
  messenger:   { label: 'Messenger',  color: 'bg-blue-100 text-blue-800',       ring: 'ring-blue-400',   Icon: Facebook },
  instagram:   { label: 'Instagram',  color: 'bg-pink-100 text-pink-800',       ring: 'ring-pink-400',   Icon: Instagram },
  tiktok_ad:   { label: 'TikTok Ad',  color: 'bg-gray-900 text-white',          ring: 'ring-gray-700',   Icon: Music2 },
  google_ad:   { label: 'Google Ad',  color: 'bg-yellow-100 text-yellow-800',   ring: 'ring-yellow-400', Icon: Target },
  appointment: { label: 'Appointment',color: 'bg-orange-100 text-orange-800',   ring: 'ring-orange-400', Icon: CalIcon },
  web:         { label: 'Note',       color: 'bg-gray-100 text-gray-700',       ring: 'ring-gray-300',   Icon: FileText },
  whatsapp:    { label: 'WhatsApp',   color: 'bg-emerald-100 text-emerald-800', ring: 'ring-emerald-400',Icon: MessageCircle }
};

const relativeTime = (date) => {
  if (!date) return '';
  const diff = (Date.now() - new Date(date).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d`;
  return new Date(date).toLocaleDateString();
};

// Robust HTML → text converter for inbox previews / regex extraction.
// Naive `replace(/<[^>]*>/g, ' ')` leaks the contents of <head>, <style>,
// <script> back into the visible string — a rich HTML email looks like
// "Subject: X </head <body { font-family:..." in the conversation list
// preview. DOMParser gives us proper traversal; we drop head + style +
// script + comments before reading textContent.
const stripHtml = (s) => {
  if (!s) return '';
  if (typeof DOMParser === 'undefined') {
    return s.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  }
  try {
    const doc = new DOMParser().parseFromString(s, 'text/html');
    doc.querySelectorAll('head, style, script, noscript').forEach((n) => n.remove());
    return (doc.body?.textContent || '').replace(/\s+/g, ' ').trim();
  } catch (_) {
    return s.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  }
};

// Curated emoji set — covers ~95% of inbox usage in customer-support
// scenarios without pulling in a 500KB picker library. Grouped roughly
// by sentiment so the operator can scan to the right shelf quickly.
const EMOJI_SET = [
  '👍', '👌', '🙏', '👋', '🤝', '👏', '💪', '🙌',
  '😊', '😀', '😁', '😂', '🤣', '😅', '😉', '😎',
  '🥰', '😍', '😘', '🤗', '🤔', '😬', '😏', '😭',
  '😢', '😔', '😞', '😡', '🤯', '😱', '🙄', '😴',
  '❤️', '🔥', '✨', '⭐', '🎉', '🎊', '🎈', '💯',
  '✅', '❌', '⚠️', '❓', '❗', '💡', '📞', '📧',
  '📅', '📍', '💰', '💵', '💳', '🏠', '🏥', '🦷',
];

// SMS length: over 160 characters the server sends numbered parts
// ("(1/2) …", utils/sms), one text each. Non-ASCII (Unicode) text may
// still be counted as more segments by carriers (70 characters each), so
// operators get a heads-up on cost.
const isUnicodeSms = (s) => /[^\x00-\x7F]/.test(s || '');
const smsSegmentInfo = (text) => ({
  len: (text || '').length,
  parts: smsPartCount(text),
  isUnicode: isUnicodeSms(text),
});

// localStorage helpers — per-browser inbox state. Sort preference stays
// local because it's a per-operator ergonomic. Read receipts and pinning
// live on the server (ConversationRead, Conversation.pinned) so they
// sync across devices and across the org.
const SORT_KEY = 'inbox.sortBy';

const ChannelBadge = ({ source, small = false }) => {
  const meta = CHANNEL_META[source] || CHANNEL_META.web;
  const { Icon } = meta;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full ${small ? 'text-[10px]' : 'text-xs'} font-medium ${meta.color}`}>
      <Icon size={small ? 10 : 12} />
      {meta.label}
    </span>
  );
};

// Lead `source` → badge, used for leads that have no conversation yet (and
// so no channel). Keeps the Inbox row informative about where the lead came
// from even before any message is exchanged.
const SOURCE_META = {
  website:      { label: 'Website',    color: 'bg-blue-100 text-blue-800',      Icon: Globe },
  phone:        { label: 'Phone',      color: 'bg-green-100 text-green-800',     Icon: Phone },
  email:        { label: 'Email',      color: 'bg-purple-100 text-purple-800',   Icon: Mail },
  social_media: { label: 'Social',     color: 'bg-pink-100 text-pink-800',       Icon: Instagram },
  referral:     { label: 'Referral',   color: 'bg-amber-100 text-amber-800',     Icon: FileText },
  google_ads:   { label: 'Google Ads', color: 'bg-yellow-100 text-yellow-800',   Icon: Target },
  other:        { label: 'Lead',       color: 'bg-gray-100 text-gray-700',       Icon: FileText },
};

const SourceBadge = ({ source, small = false }) => {
  const meta = SOURCE_META[source] || SOURCE_META.other;
  const { Icon } = meta;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full ${small ? 'text-[10px]' : 'text-xs'} font-medium ${meta.color}`}>
      <Icon size={small ? 10 : 12} />
      {meta.label}
    </span>
  );
};

// ── WhatsApp message attachments ──────────────────────────────────────
// Files live behind the authenticated lead-attachment routes, so nothing
// here is a bare <img src>: images pull the 320px thumbnail as a blob,
// voice notes stream the file into an <audio> element, everything else is
// a chip that downloads on click.
const formatBytes = (n) => {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const MessageAttachment = ({ leadId, att, isOutbound, onPreview }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const type = att.contentType || '';
  const isImage = /^image\//.test(type);
  const isAudio = /^audio\//.test(type);
  const canFetch = !!(leadId && att.attachmentId);

  useEffect(() => {
    if (!canFetch || (!isImage && !isAudio)) return undefined;
    let cancelled = false;
    let url = null;
    const req = isImage
      ? leadsAPI.getAttachmentThumbnail(leadId, att.attachmentId)
      : leadsAPI.downloadAttachment(leadId, att.attachmentId);
    req.then((r) => {
      if (cancelled) return;
      url = URL.createObjectURL(r.data);
      setBlobUrl(url);
    }).catch(() => {});
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [leadId, att.attachmentId, canFetch, isImage, isAudio]);

  const download = async (e) => {
    e?.stopPropagation?.();
    if (!canFetch || busy) return;
    setBusy(true);
    try {
      const r = await leadsAPI.downloadAttachment(leadId, att.attachmentId);
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.filename || 'attachment';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (_) {
      toast.error('Could not download attachment');
    } finally {
      setBusy(false);
    }
  };

  if (isImage) {
    return (
      <button
        type="button"
        onClick={() => (onPreview && canFetch ? onPreview({ leadId, att }) : download())}
        title={`${att.filename || 'Image'} — click to view`}
        className="block rounded-xl overflow-hidden border border-gray-200 bg-gray-50 max-w-[240px] hover:opacity-90"
      >
        {blobUrl
          ? <img src={blobUrl} alt={att.filename || 'Image'} className="block max-h-[240px] w-auto object-cover" />
          : <div className="w-[160px] h-[120px] flex items-center justify-center text-gray-400"><ImageIcon size={22} /></div>}
      </button>
    );
  }
  if (isAudio) {
    return (
      <div className={`flex items-center gap-2 rounded-xl border px-2 py-1.5 ${isOutbound ? 'bg-primary-50 border-primary-100' : 'bg-white border-gray-200'}`}>
        {blobUrl
          ? <audio controls src={blobUrl} className="h-8 max-w-[240px]" preload="metadata" />
          : <span className="text-xs text-gray-500 px-2">Loading voice message…</span>}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={download}
      disabled={!canFetch || busy}
      className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-xs text-left max-w-[260px] ${isOutbound ? 'bg-primary-50 border-primary-100 text-primary-900' : 'bg-white border-gray-200 text-gray-800'} hover:bg-gray-50 disabled:opacity-60`}
    >
      <FileText size={16} className="shrink-0" />
      <span className="min-w-0">
        <span className="block truncate font-medium">{att.filename || 'Attachment'}</span>
        <span className="block text-[10px] text-gray-500">{formatBytes(att.size)}{att.size ? ' · ' : ''}{busy ? 'Downloading…' : 'Download'}</span>
      </span>
      <Download size={12} className="shrink-0 text-gray-400" />
    </button>
  );
};

// WhatsApp delivery ticks for outbound bubbles: ✓ sent, ✓✓ delivered,
// blue ✓✓ read, red ! failed.
const WaStatusTicks = ({ status, failureReason }) => {
  if (!status) return null;
  if (status === 'failed') {
    return <span className="inline-flex items-center gap-0.5 text-red-600" title={failureReason || 'Not delivered'}><AlertCircle size={11} /> failed</span>;
  }
  if (status === 'sent') return <Check size={12} className="text-gray-400" title="Sent" />;
  return <CheckCheck size={12} className={status === 'read' ? 'text-sky-500' : 'text-gray-400'} title={status === 'read' ? 'Read' : 'Delivered'} />;
};

// Full-size image viewer for message attachments. Fetches the original
// through the authenticated download route (the bubble only holds the
// 320px thumbnail), closes on Esc / backdrop click, and keeps a download
// button so saving the file is still one click away.
const ImageLightbox = ({ leadId, att, onClose }) => {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    leadsAPI.downloadAttachment(leadId, att.attachmentId).then((r) => {
      if (cancelled) return;
      objectUrl = URL.createObjectURL(r.data);
      setUrl(objectUrl);
    }).catch(() => { if (!cancelled) setFailed(true); });
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      cancelled = true;
      window.removeEventListener('keydown', onKey);
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [leadId, att.attachmentId, onClose]);

  const save = (e) => {
    e.stopPropagation();
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = att.filename || 'image';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/85 flex flex-col"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div className="flex items-center justify-between gap-3 px-4 py-3 text-white/90 text-sm" onClick={(e) => e.stopPropagation()}>
        <span className="truncate">{att.filename || 'Image'}{att.size ? ` · ${formatBytes(att.size)}` : ''}</span>
        <div className="flex items-center gap-1 shrink-0">
          <button type="button" onClick={save} disabled={!url} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-40" title="Download">
            <Download size={15} /> <span className="hidden sm:inline">Download</span>
          </button>
          <button type="button" onClick={onClose} className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-white/10 hover:bg-white/20" title="Close (Esc)">
            <X size={18} />
          </button>
        </div>
      </div>
      <div className="flex-1 min-h-0 flex items-center justify-center p-3 sm:p-6">
        {failed ? (
          <div className="text-white/80 text-sm">Could not load this image.</div>
        ) : url ? (
          <img
            src={url}
            alt={att.filename || 'Image'}
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <Loader2 size={28} className="animate-spin text-white/70" />
        )}
      </div>
    </div>
  );
};

const formatRemaining = (expiresAt) => {
  if (!expiresAt) return '';
  const ms = Math.max(0, new Date(expiresAt).getTime() - Date.now());
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const WA_UPLOAD_MAX = 10 * 1024 * 1024; // matches the lead-attachment upload cap
// Channels whose composer accepts file attachments. WhatsApp / SMS (MMS) /
// Messenger reference lead attachments by id; email inlines base64 the
// way the Lead Detail send-email modal does.
const ATTACH_CHANNELS = new Set(['whatsapp', 'sms', 'messenger', 'email']);

const Inbox = () => {
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();
  const [channelFilter, setChannelFilter] = useState('all');
  // Message id whose AI Auto-Reply audit panel is expanded.
  const [openAiAuditId, setOpenAiAuditId] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null); // conversation _id
  const [sortBy, setSortBy] = useState(() => localStorage.getItem(SORT_KEY) || 'newest');

  // Deep link from Lead Detail ("Open in Inbox"): /inbox?leadId=<id>.
  // The target lead's row may live pages below the initial 50, so it is
  // fetched separately (server-side leadId filter) and spliced into the
  // list. The param is copied into state and stripped from the URL once
  // the selection is applied, so back/forward and manual row clicks
  // behave normally afterwards.
  const [searchParams, setSearchParams] = useSearchParams();
  const [deepLinkLeadId, setDeepLinkLeadId] = useState(null);
  const [appliedDeepLink, setAppliedDeepLink] = useState(null);
  useEffect(() => {
    const p = searchParams.get('leadId');
    if (p) setDeepLinkLeadId(p);
  }, [searchParams]);
  const deepLinkPending = !!deepLinkLeadId && appliedDeepLink !== deepLinkLeadId;

  const persistSort = (next) => { setSortBy(next); try { localStorage.setItem(SORT_KEY, next); } catch (_) {} };

  // Conversation is unread when its lastMessage is inbound AND the
  // operator's readAt is older than lastMessageAt (or missing).
  const isUnread = (c) => {
    if (!c.lastMessage || c.lastMessage.direction !== 'inbound') return false;
    if (!c.lastMessageAt) return false;
    const seen = c.readAt;
    return !seen || new Date(seen) < new Date(c.lastMessageAt);
  };
  const [composerMode, setComposerMode] = useState('sms'); // 'email' | 'sms'
  const [composerBody, setComposerBody] = useState('');
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [threadSearch, setThreadSearch] = useState('');
  const [showThreadSearch, setShowThreadSearch] = useState(false);
  const composerRef = useRef(null);
  const [settingsRequiredModal, setSettingsRequiredModal] = useState({
    isOpen: false,
    type: 'email'
  });
  const chatEndRef = useRef(null);
  // Scroll container for the message list. We attach scroll handlers
  // here to trigger lazy-load of older pages when the user reaches the
  // top, and to preserve visual position after the prepend.
  const chatScrollRef = useRef(null);
  const prevScrollHeightRef = useRef(0);

  // SMTP / SMS configuration probes (cached)
  const { data: smtpSettings } = useQuery(
    ['smtp-settings'],
    () => api.get('/smtp/settings').then((r) => r.data),
    { staleTime: 60 * 1000, retry: false }
  );
  const { data: smsSettings } = useQuery(
    ['voip-status'],
    () => api.get('/voip/status').then((r) => r.data),
    { staleTime: 60 * 1000, retry: false }
  );
  const { data: waStatus } = useQuery(
    ['whatsapp-status'],
    () => api.get('/inbound-settings/whatsapp/status').then((r) => r.data),
    { staleTime: 60 * 1000, retry: false }
  );
  const isSmtpConfigured = !!smtpSettings?.isConfigured;
  const isSmsConfigured = smsSettings?.provider === 'voipms' && !!smsSettings?.isConfigured;
  const isWhatsappConfigured = !!waStatus?.isConfigured;
  const canManageSettings = currentUser?.permissions?.manage_smtp_settings !== false;

  // WhatsApp composer extras: pending attachments (new files to upload or
  // existing lead files), plus template state for when the 24h window is
  // closed. Reset whenever the selected lead changes.
  const [imagePreview, setImagePreview] = useState(null); // { leadId, att }
  const [waPending, setWaPending] = useState([]); // [{ kind:'file', file } | { kind:'existing', id, name, size }]
  const [waAttachMenuOpen, setWaAttachMenuOpen] = useState(false);
  const waFileInputRef = useRef(null);
  const [waTemplate, setWaTemplate] = useState(null);
  const [waParams, setWaParams] = useState([]);
  const [waHeaderParam, setWaHeaderParam] = useState('');

  // Conversations list — paginated 50 at a time, sorted server-side by
  // lastMessageAt DESC. Operators on big tenants (hundreds of leads)
  // would otherwise pay the full hydrate-everything cost on every
  // Inbox open. Older pages are fetched lazily when the list scrolls
  // near the bottom.
  const {
    data: conversationsInfinite,
    isLoading,
    fetchNextPage: fetchMoreConversations,
    hasNextPage: hasMoreConversations,
    isFetchingNextPage: isLoadingMoreConversations,
  } = useInfiniteQuery(
    ['conversations', channelFilter, search],
    ({ pageParam = 0 }) =>
      api.get('/conversations', {
        params: { channel: channelFilter, q: search, skip: pageParam, limit: 50 },
      }).then((r) => r.data),
    {
      getNextPageParam: (lastPage) => lastPage?.nextSkip ?? undefined,
      staleTime: 10 * 1000,
    }
  );
  // Deep-linked lead's row(s), fetched directly so selection doesn't
  // depend on how far the paginated list has scrolled.
  const deepLinkConvsQuery = useQuery(
    ['conversations-deeplink', deepLinkLeadId],
    () =>
      api.get('/conversations', {
        params: { leadId: deepLinkLeadId, limit: 50 },
      }).then((r) => r.data),
    { enabled: !!deepLinkLeadId, staleTime: 10 * 1000 }
  );

  const conversations = useMemo(() => {
    const rows = conversationsInfinite?.pages
      ? conversationsInfinite.pages.flatMap((p) => p?.rows || [])
      : [];
    // Splice in the deep-linked lead's rows when pagination hasn't
    // reached them yet. Dedupe by _id — the group-by-lead reducer below
    // would otherwise count the same conversation as its own sibling.
    const extra = deepLinkConvsQuery.data?.rows || [];
    if (!extra.length) return rows;
    const seen = new Set(rows.map((r) => r._id));
    return [...rows, ...extra.filter((r) => !seen.has(r._id))];
  }, [conversationsInfinite, deepLinkConvsQuery.data]);

  // Walk every cached page when optimistically updating a single row
  // — useInfiniteQuery stores data as { pages: [...] }, not a flat
  // array, so the old setQueriesData mapper would no-op silently.
  const patchConversationCache = (predicate, patch) => {
    queryClient.setQueriesData(['conversations'], (prev) => {
      if (!prev?.pages) return prev;
      return {
        ...prev,
        pages: prev.pages.map((page) => ({
          ...page,
          rows: (page?.rows || []).map((r) => (predicate(r) ? { ...r, ...patch } : r)),
        })),
      };
    });
  };

  const togglePinMutation = useMutation(
    ({ id, pinned }) => api.post(`/conversations/${id}/pin`, { pinned }).then((r) => r.data),
    {
      onMutate: ({ id, pinned }) => {
        patchConversationCache((c) => c._id === id, { pinned });
      },
      onError: () => queryClient.invalidateQueries(['conversations']),
    }
  );
  const togglePin = (conv) => togglePinMutation.mutate({ id: conv._id, pinned: !conv.pinned });

  // Auto-select the newest conversation on desktop. On mobile we keep the
  // list view as the landing screen so the user explicitly picks a thread.
  useEffect(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) return;
    if (deepLinkPending) return; // deep link owns the first selection
    if (conversations && conversations.length > 0 && !selectedId) {
      setSelectedId(conversations[0]._id);
    }
  }, [conversations, selectedId, deepLinkPending]);

  // Mark a conversation as read the moment its row is opened. Optimistic
  // cache update keeps the UI snappy; server upsert syncs the timestamp
  // across the user's other devices.
  const markReadMutation = useMutation(
    (convId) => api.post(`/conversations/${convId}/read`).then((r) => r.data),
    {
      onMutate: (convId) => {
        const stamp = new Date().toISOString();
        patchConversationCache((c) => c._id === convId, { readAt: stamp });
      },
    }
  );
  // The optimistic onMutate above patches the conversations cache, which
  // changes the array reference and re-runs this effect. The `>=` guard
  // *should* break the loop on the second pass, but SSE updates can bump
  // lastMessageAt by a few ms between cache patch and re-render, which
  // makes the guard fail and the mutation fires again. Result: a tight
  // `/conversations/:id/read` POST loop that freezes the page.
  //
  // Belt-and-braces fix: track which (convId, lastMessageAt) tuples have
  // already triggered the mutation in this mount. The set re-keys on a
  // new inbound message (lastMessageAt changes), so genuine new mail
  // still gets marked once.
  const markedReadKeysRef = useRef(new Set());
  useEffect(() => {
    if (!selectedId) return;
    const conv = conversations?.find((c) => c._id === selectedId);
    if (!conv) return;
    if (!conv.lastMessage || conv.lastMessage.direction !== 'inbound') return;
    if (!conv.lastMessageAt) return;
    const key = `${selectedId}:${conv.lastMessageAt}`;
    if (markedReadKeysRef.current.has(key)) return;
    if (conv.readAt && new Date(conv.readAt) >= new Date(conv.lastMessageAt)) {
      markedReadKeysRef.current.add(key);
      return;
    }
    markedReadKeysRef.current.add(key);
    markReadMutation.mutate(selectedId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, conversations]);

  // Apply sort + pinned-on-top to whatever the server returned. Server
  // sorts newest first by default; we then overlay the operator's choice.
  // Then collapse multi-channel rows for the same lead into one entry —
  // the Inbox list is lead-centric, channel is a secondary detail
  // surfaced as inline badges.
  const sortedConversations = useMemo(() => {
    const raw = Array.isArray(conversations) ? [...conversations] : [];

    // Group-by-lead reducer: walk newest-first so the first conv we see
    // for a given lead becomes the primary. The primary's _id is kept
    // intact so existing per-conv mutations (markRead, pin, send) keep
    // routing to a real Conversation document; siblings' ids ride along
    // in `siblingConversationIds` for future "mark whole lead read" use.
    const byLastMessageAt = raw.slice().sort((a, b) =>
      new Date(b.lastMessageAt || 0) - new Date(a.lastMessageAt || 0)
    );
    const byLead = new Map();
    for (const c of byLastMessageAt) {
      const key = String(c.leadId);
      const primaryChan = c.actualChannel || c.channel;
      const existing = byLead.get(key);
      if (!existing) {
        byLead.set(key, {
          ...c,
          channelBadges: [primaryChan].filter(Boolean),
          siblingConversationIds: [],
          // Aggregate "row unread" — true if ANY channel for this lead
          // has an unread message. Calculated below after the loop.
          _anyUnread: isUnread(c),
          // Pin floats the row when ANY channel is pinned.
          _anyPinned: !!c.pinned,
        });
        continue;
      }
      if (primaryChan && !existing.channelBadges.includes(primaryChan)) {
        existing.channelBadges.push(primaryChan);
      }
      existing.siblingConversationIds.push(c._id);
      if (isUnread(c)) existing._anyUnread = true;
      if (c.pinned) existing._anyPinned = true;
    }
    let list = Array.from(byLead.values()).map((row) => ({
      ...row,
      // Override the bottom-line fields with the aggregated signals so
      // the row UI (which still reads c.pinned / c.readAt) reflects the
      // merged truth rather than the primary conv's solo state.
      pinned: row._anyPinned,
      readAt: row._anyUnread ? null : row.readAt,
    }));

    if (sortBy === 'unread') {
      list.sort((a, b) => Number(isUnread(b)) - Number(isUnread(a)) ||
        new Date(b.lastMessageAt || 0) - new Date(a.lastMessageAt || 0));
    } else if (sortBy === 'oldest') {
      list.sort((a, b) => new Date(a.lastMessageAt || 0) - new Date(b.lastMessageAt || 0));
    }
    // Pinned always float to the top, regardless of sortBy.
    list.sort((a, b) => Number(!!b.pinned) - Number(!!a.pinned));
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversations, sortBy]);

  // Apply the deep link once the lead's grouped row is available (the
  // selection targets the lead-grouped row so the merged multi-channel
  // timeline renders, same as a manual click). If the lead isn't
  // visible to this user (bad id, archived, access filter), release the
  // hold so normal auto-select takes over.
  useEffect(() => {
    if (!deepLinkPending) return;
    const row = (sortedConversations || []).find(
      (c) => String(c.leadId) === String(deepLinkLeadId)
    );
    const cameUpEmpty =
      deepLinkConvsQuery.isFetched && !(deepLinkConvsQuery.data?.rows || []).length;
    if (!row && !cameUpEmpty) return; // still loading — keep waiting
    if (row) setSelectedId(row._id);
    setAppliedDeepLink(deepLinkLeadId);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('leadId');
        return next;
      },
      { replace: true }
    );
  }, [
    deepLinkPending,
    deepLinkLeadId,
    sortedConversations,
    deepLinkConvsQuery.isFetched,
    deepLinkConvsQuery.data,
    setSearchParams,
  ]);

  // Clear unread Inbox badge as soon as the page opens
  useEffect(() => {
    notificationsAPI.markTypeAsRead('inbound_message')
      .then(() => {
        queryClient.invalidateQueries('notifications');
        queryClient.invalidateQueries('unread-notifications');
      })
      .catch(() => {});
  }, [queryClient]);

  // Real-time refresh: listen for note/conversation events via SSE
  useEffect(() => {
    const _token = localStorage.getItem('token');
    if (!_token) return;
    const es = new EventSource(`${API_BASE_URL}/events?token=${encodeURIComponent(_token)}`);
    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (['note_added', 'note_deleted', 'conversation_updated'].includes(data.type)) {
          queryClient.invalidateQueries(['conversations']);
          queryClient.invalidateQueries(['conv-thread']);
          // Sibling-thread queries (one per channel for the selected
          // lead) live under a distinct key, so they need their own
          // invalidation to pick up new inbound messages.
          queryClient.invalidateQueries(['conv-thread-sibling']);
          if (data.leadId) {
            queryClient.invalidateQueries(['lead', data.leadId]);
          }
        }
      } catch (_) {}
    };
    return () => es.close();
  }, [queryClient]);

  // Selected conversation row from the LEAD-GROUPED list. Important: we
  // read from sortedConversations rather than the raw conversations
  // array because the row the operator clicked carries
  // `siblingConversationIds` — the other channels' Conversation _ids for
  // the same lead. Reading the ungrouped flatMap would lose that
  // metadata and the merged-thread useQueries below would never fetch
  // sibling messages.
  const selectedConv = useMemo(
    () => (sortedConversations || []).find((c) => c._id === selectedId) || null,
    [sortedConversations, selectedId]
  );
  const selectedLeadId = selectedConv?.leadId || null;

  const { data: leadDetail } = useQuery(
    ['lead', selectedLeadId],
    () => leadsAPI.getLead(selectedLeadId),
    { enabled: !!selectedLeadId, refetchOnWindowFocus: true }
  );
  const lead = leadDetail?.data || leadDetail;
  // FCFS claim flow specifically for tap-on-phone interactions inside
  // the form card. Send-time claiming is enforced by the server (see
  // claimOrAuthorize), so the courtesy modal for send lives separately
  // as `claimConfirm`; this hook owns dial-flow claims.
  const phoneClaim = useLeadClaim({ lead, currentUser });

  // Phase B: the dedicated Message endpoint is paginated — the newest
  // 50 messages come first, then scrolling up fetches the previous
  // 50, and so on. Long threads (hundreds of messages) no longer drop
  // the entire history into the wire on first open.
  const {
    data: threadInfinite,
    fetchNextPage: fetchOlderMessages,
    hasNextPage: hasOlderMessages,
    isFetchingNextPage: isLoadingOlder,
    isLoading: isThreadLoading,
  } = useInfiniteQuery(
    ['conv-thread', selectedId],
    ({ pageParam }) => {
      const url = pageParam
        ? `/conversations/${selectedId}/messages?before=${encodeURIComponent(pageParam)}`
        : `/conversations/${selectedId}/messages`;
      return api.get(url).then((r) => r.data);
    },
    {
      // No-message leads have a synthetic id ("lead:<id>") with no
      // Conversation document — skip the fetch (it would 404). The empty
      // timeline + composer renders from the lead alone.
      enabled: !!selectedId && !selectedConv?.noConversation,
      // Each page returns chronological messages; pageParam for the
      // next (older) page is the sentAt of the oldest message we have.
      getNextPageParam: (lastPage) => {
        if (!lastPage?.hasMore) return undefined;
        const oldest = lastPage.messages?.[0];
        return oldest?.sentAt || undefined;
      },
      refetchOnWindowFocus: true,
      staleTime: 5 * 1000,
    }
  );
  // The first page (data.pages[0]) holds the *newest* slice and carries
  // the conversation envelope. Older pages come after. Flatten in
  // reverse-page order to land oldest → newest in the rendered list.
  const threadData = useMemo(() => {
    if (!threadInfinite?.pages?.length) return null;
    const all = [...threadInfinite.pages].reverse().flatMap((p) => p.messages || []);
    return { conversation: threadInfinite.pages[0].conversation, messages: all };
  }, [threadInfinite]);

  // Sibling conversations from the dedupe — when the selected row
  // represents a lead with multiple channel conversations (e.g. a phone
  // call thread AND an SMS thread for the same person), fetch the
  // sibling threads in parallel and merge their messages into the
  // primary timeline. Keeps the lead-centric Inbox UX honest: clicking a
  // row shows every message with that person across every channel, in
  // one chronological column.
  //
  // Pagination of siblings is deferred — we ask for the most recent 200
  // messages per sibling, which covers SMS threads comfortably. A lead
  // with a thousand-message sibling thread would lose the oldest tail;
  // that's a follow-up if anyone hits it in practice.
  const siblingIds = selectedConv?.siblingConversationIds || [];
  const siblingQueries = useQueries(
    siblingIds.map((sid) => ({
      queryKey: ['conv-thread-sibling', sid],
      queryFn: () => api.get(`/conversations/${sid}/messages?limit=200`).then((r) => r.data),
      enabled: !!sid && !!selectedId,
      staleTime: 5 * 1000,
      refetchOnWindowFocus: true,
    }))
  );

  const messages = useMemo(() => {
    const primary = threadData?.messages || [];
    const sibling = siblingQueries.flatMap((q) => q.data?.messages || []);
    const all = [...primary, ...sibling];
    // Sort by sentAt to interleave channels correctly.
    all.sort((a, b) => new Date(a.sentAt || a.createdAt || 0) - new Date(b.sentAt || b.createdAt || 0));
    // Normalise to the legacy shape the chat bubbles already render.
    return all.map((m) => ({
      ...m,
      source: m.channel,
      createdAt: m.sentAt || m.createdAt,
    }));
  }, [threadData, siblingQueries]);

  // True while a freshly-opened conversation is still assembling — the
  // primary page and/or the sibling-channel threads are loading. Messages
  // stream in over several ticks (primary first, then each sibling), and
  // the sort interleaves older sibling messages above the fold. We hold
  // the scroll-to-bottom latch open until this settles so late arrivals
  // don't strand the view mid-thread on the first/oldest message.
  const threadSettling = isThreadLoading || siblingQueries.some((q) => q.isLoading);

  // Auto-derived reply subject for the current email thread. The customer
  // chose the subject when they wrote in; we just prefix "Re:" and ship.
  // Operators don't have to type subjects manually any more — the input
  // box that used to live above the composer is gone.
  const replySubject = useMemo(() => {
    if (selectedConv?.channel !== 'email') return '';
    const lastInbound = [...messages]
      .filter((m) => m.direction === 'inbound')
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
    if (lastInbound?.subject) {
      let s = String(lastInbound.subject);
      if (!/^re:\s/i.test(s)) s = `Re: ${s}`;
      return s.slice(0, 200);
    }
    if (!lastInbound) return '';
    const raw = lastInbound.content || '';
    let subject = '';
    // Preferred: IMAP poller wraps the subject in a structured div.
    // Anchor on that exact HTML so we don't accidentally swallow the
    // entire body (which a /Subject: ... \n/ regex over stripHtml does
    // for HTML emails since they have no real newlines).
    const structured = raw.match(/<div class="email-subject"><b>Subject:<\/b>\s*([^<]+)<\/div>/i);
    if (structured) {
      subject = structured[1].trim();
    } else {
      // Legacy / plain-text content: fall back to a stop-at-newline
      // regex run on stripped text. Cap at the first run of two
      // spaces too — HTML stripping collapses them, helps when there's
      // no newline at all.
      const stripped = stripHtml(raw);
      const m = stripped.match(/Subject:\s*(.{1,200}?)(?:\s{2,}|$)/i);
      if (m) subject = m[1].trim();
    }
    if (!subject) return '';
    if (!/^re:\s/i.test(subject)) subject = `Re: ${subject}`;
    return subject.slice(0, 200);
  }, [selectedConv, messages]);

  // Filter the visible messages by the in-thread search term. Empty term
  // shows everything; non-empty matches case-insensitively against the
  // stripped HTML so HTML markup doesn't poison the search.
  const visibleMessages = useMemo(() => {
    const q = threadSearch.trim().toLowerCase();
    if (!q) return messages;
    return messages.filter((m) => stripHtml(m.content).toLowerCase().includes(q));
  }, [messages, threadSearch]);

  const unreadCount = useMemo(
    () => (sortedConversations || []).filter(isUnread).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sortedConversations]
  );

  const markAllReadMutation = useMutation(
    () => api.post('/conversations/read-all').then((r) => r.data),
    {
      onMutate: () => {
        const stamp = new Date().toISOString();
        patchConversationCache(() => true, { readAt: stamp });
      },
      onSuccess: () => toast.success('All conversations marked as read'),
      onError: () => toast.error('Could not mark conversations as read'),
    }
  );
  const markAllRead = () => {
    if (!conversations.length) return;
    markAllReadMutation.mutate();
  };

  // j/k (or ArrowDown / ArrowUp with no modifier) cycles through the
  // sorted conversation list. Skips when the user is typing in an input
  // — otherwise navigation steals every keystroke from the composer.
  useEffect(() => {
    const handler = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      if (['input', 'textarea', 'select'].includes(tag) || e.target?.isContentEditable) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const list = sortedConversations;
      if (!list?.length) return;
      const idx = list.findIndex((c) => c._id === selectedId);
      if (e.key === 'j' || e.key === 'ArrowDown') {
        e.preventDefault();
        const next = list[Math.min(list.length - 1, Math.max(0, idx + 1))];
        if (next) setSelectedId(next._id);
      } else if (e.key === 'k' || e.key === 'ArrowUp') {
        e.preventDefault();
        const prev = list[Math.max(0, idx - 1)];
        if (prev) setSelectedId(prev._id);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [sortedConversations, selectedId]);

  const insertAtCursor = (text) => {
    const el = composerRef.current;
    if (!el) {
      setComposerBody((b) => b + text);
      return;
    }
    const start = el.selectionStart ?? composerBody.length;
    const end = el.selectionEnd ?? composerBody.length;
    const next = composerBody.slice(0, start) + text + composerBody.slice(end);
    setComposerBody(next);
    requestAnimationFrame(() => {
      el.focus();
      const cursor = start + text.length;
      el.setSelectionRange(cursor, cursor);
    });
  };

  // Tracks how many messages the chat had on the previous render so
  // we can distinguish "new message arrived at the bottom" (scroll
  // down) from "older page just prepended at the top" (preserve
  // position).
  // Latch that forces a jump-to-bottom when a conversation is opened. Set
  // on every selectedId change and held until the thread (primary +
  // siblings) finishes loading, so multi-stage message arrival can't leave
  // the view stranded on the oldest message.
  const needsBottomAnchorRef = useRef(true);
  useEffect(() => {
    needsBottomAnchorRef.current = true;
  }, [selectedId]);

  useEffect(() => {
    const el = chatScrollRef.current;
    if (!el) return;

    // Conversation just opened — keep snapping to the latest message on
    // every render until all the message slices have landed. Only release
    // the latch once loading has settled so a late sibling page doesn't
    // bounce us back up the thread.
    if (needsBottomAnchorRef.current) {
      if (messages.length > 0) {
        requestAnimationFrame(() => {
          if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: 'auto', block: 'end' });
        });
      }
      if (!threadSettling && messages.length > 0) needsBottomAnchorRef.current = false;
      return;
    }

    // Older page just got prepended: keep the user's eyeline on
    // whatever message they were looking at by adding the height
    // difference back to scrollTop.
    if (prevScrollHeightRef.current && el.scrollHeight > prevScrollHeightRef.current) {
      const delta = el.scrollHeight - prevScrollHeightRef.current;
      el.scrollTop = el.scrollTop + delta;
      prevScrollHeightRef.current = 0;
      return;
    }

    // A new live message landed at the bottom — only autoscroll if
    // the user was already at (or close to) the bottom. Otherwise
    // they're reading older content and we'd yank them away.
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    if (nearBottom && chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [messages.length, selectedId, threadSettling]);

  // Lazy-load older messages when the user scrolls to (near) the top
  // of the chat pane. Capture the scrollHeight before the fetch so the
  // post-prepend effect above can restore the visual position.
  const handleChatScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollTop < 80 && hasOlderMessages && !isLoadingOlder) {
      prevScrollHeightRef.current = el.scrollHeight;
      fetchOlderMessages();
    }
  };

  // DM availability flags — derived up here so the composerMode
  // useEffect below can depend on them (TDZ otherwise).
  const messengerTag = useMemo(() => {
    return (lead?.tags || []).find(
      (t) => typeof t === 'string' && /^meta:(psid|igsid):/.test(t)
    ) || null;
  }, [lead]);
  const hasLeadgenTag = useMemo(() => {
    return (lead?.tags || []).some(
      (t) => typeof t === 'string' && /^meta:leadgen:/.test(t)
    );
  }, [lead]);
  // Pull the leadgen id off the lead's tag so Click-to-Messenger DMs
  // (whose content has no `leadgen_id:` line) still surface the same
  // submission reference in the form card footer.
  const leadgenIdFromTags = useMemo(() => {
    const tag = (lead?.tags || []).find(
      (t) => typeof t === 'string' && /^meta:leadgen:/.test(t)
    );
    return tag ? tag.split(':')[2] : null;
  }, [lead]);
  const leadMetaPlatform = useMemo(() => {
    const cf = lead?.customFields;
    if (!cf) return null;
    return (typeof cf.get === 'function' ? cf.get('meta_platform') : cf.meta_platform) || null;
  }, [lead]);
  const dmPlatform =
    messengerTag?.startsWith('meta:igsid:') ? 'instagram'
    : messengerTag?.startsWith('meta:psid:') ? 'messenger'
    : leadMetaPlatform === 'ig' ? 'instagram'
    : leadMetaPlatform === 'fb' ? 'messenger'
    : (hasLeadgenTag ? 'messenger' : null);
  const dmAvailable = !!messengerTag || hasLeadgenTag;
  const isInstagramThread = dmPlatform === 'instagram';
  const messengerLabel = isInstagramThread ? 'Instagram' : 'Messenger';

  // Composer mode picks the most natural reply channel for the
  // conversation. Priority:
  //   1) actualChannel of the Conversation row (existing thread)
  //   2) DM availability — Click-to-Messenger Lead Ad without a PSID
  //      tag yet still has a thread waiting on Meta's side; we'll
  //      surface a Messenger/Instagram option proactively
  //   3) email / sms fallbacks
  useEffect(() => {
    if (!selectedConv || !lead) return;
    const canEmail = !!lead.email;
    // A lead who texted STOP can still get WhatsApp, never SMS.
    const hasPhone = !!lead.phone;
    const canSms = hasPhone && !smsOptOutNotice(lead);

    const channelFromSource = (s) => {
      if (s === 'email') return canEmail ? 'email' : null;
      if (s === 'whatsapp') {
        if (hasPhone && isWhatsappConfigured) return 'whatsapp';
        return canSms ? 'sms' : null;
      }
      if (s === 'sms' || s === 'phone') return canSms ? 'sms' : null;
      if (s === 'messenger' || s === 'instagram') return dmAvailable ? 'messenger' : null;
      return null;
    };

    let next = channelFromSource(selectedConv.actualChannel || selectedConv.channel);
    if (!next) {
      if (dmAvailable) next = 'messenger'; // unified mode; dmPlatform decides icon/label
      else if (canEmail) next = 'email';
      else if (canSms) next = 'sms';
      else next = 'sms';
    }
    setComposerMode(next);
  }, [selectedConv, lead, dmAvailable, isWhatsappConfigured]);

  // Upload any new files to the lead, then return every pending item as a
  // lead attachment id (new uploads come back last in the returned list).
  const resolvePendingAttachmentIds = async (leadId) => {
    let ids = waPending.filter((p) => p.kind === 'existing').map((p) => p.id);
    const files = waPending.filter((p) => p.kind === 'file').map((p) => p.file);
    if (files.length) {
      const r = await leadsAPI.uploadAttachments(leadId, files);
      const all = r.data?.attachments || [];
      ids = [...ids, ...all.slice(-files.length).map((a) => String(a._id))];
    }
    return ids;
  };

  const sendEmailMutation = useMutation(
    async ({ leadId, payload }) => {
      const attachmentIds = waPending.length ? await resolvePendingAttachmentIds(leadId) : [];
      return leadsAPI.sendEmail(leadId, { ...payload, attachmentIds });
    },
    {
      onSuccess: () => {
        toast.success('Email sent');
        setComposerBody('');
        setWaPending([]);
        queryClient.invalidateQueries(['lead', selectedLeadId]);
        queryClient.invalidateQueries(['conv-thread', selectedId]);
        queryClient.invalidateQueries(['conversations']);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Send failed')
    }
  );

  const sendSmsMutation = useMutation(
    async ({ leadId, payload }) => {
      const attachmentIds = waPending.length ? await resolvePendingAttachmentIds(leadId) : [];
      return leadsAPI.sendSMS(leadId, { ...payload, attachmentIds });
    },
    {
      onSuccess: (r) => {
        toast.success(r?.data?.smsData?.attachmentCount ? 'MMS sent' : 'SMS sent');
        setComposerBody('');
        setWaPending([]);
        queryClient.invalidateQueries(['lead', selectedLeadId]);
        queryClient.invalidateQueries(['conv-thread', selectedId]);
        queryClient.invalidateQueries(['conversations']);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Send failed')
    }
  );

  const sendWhatsappMutation = useMutation(
    async ({ leadId, payload }) => {
      // New files are uploaded to the lead first (they become lead
      // attachments, which is also where the send route reads them from),
      // then referenced by id together with any existing files picked.
      const attachmentIds = waPending.length ? await resolvePendingAttachmentIds(leadId) : [];
      return leadsAPI.sendWhatsApp(leadId, { ...payload, attachmentIds });
    },
    {
      onSuccess: () => {
        toast.success('WhatsApp message sent');
        setComposerBody('');
        setWaPending([]);
        queryClient.invalidateQueries(['lead', selectedLeadId]);
        queryClient.invalidateQueries(['conv-thread', selectedId]);
        queryClient.invalidateQueries(['conversations']);
      },
      // Surface the backend error verbatim — most often the 24h window
      // message mapped from Meta code 131047.
      onError: (err) => toast.error(err.response?.data?.error || 'Send failed')
    }
  );

  const sendWhatsappTemplateMutation = useMutation(
    () => leadsAPI.sendWhatsAppTemplate(selectedLeadId, {
      name: waTemplate.name,
      language: waTemplate.language,
      params: waParams,
      headerParam: waHeaderNeedsParam ? waHeaderParam : '',
      bodyText: waTemplate.bodyText,
      phoneField: 'phone',
    }),
    {
      onSuccess: () => {
        toast.success('WhatsApp template sent');
        setWaTemplate(null);
        setWaParams([]);
        setWaHeaderParam('');
        queryClient.invalidateQueries(['lead', selectedLeadId]);
        queryClient.invalidateQueries(['conv-thread', selectedId]);
        queryClient.invalidateQueries(['conversations']);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Template send failed')
    }
  );

  const sendMessengerMutation = useMutation(
    async ({ leadId, payload }) => {
      const attachmentIds = waPending.length ? await resolvePendingAttachmentIds(leadId) : [];
      return leadsAPI.sendMessenger(leadId, { ...payload, attachmentIds });
    },
    {
      onSuccess: () => {
        toast.success('Reply sent');
        setComposerBody('');
        setWaPending([]);
        queryClient.invalidateQueries(['lead', selectedLeadId]);
        queryClient.invalidateQueries(['conv-thread', selectedId]);
        queryClient.invalidateQueries(['conversations']);
      },
      onError: (err) => {
        // Surface Meta's error verbatim — most often the 24h window message.
        toast.error(err.response?.data?.error || 'Send failed');
      }
    }
  );

  // ✨ Suggest reply — sends the conversation + matching KB entries to
  // the AI assist endpoint, opens the AIReplyPanel with 3 drafts +
  // confidence. The operator picks one, optionally edits, then sends.
  // Deliberately never auto-sends.
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [aiError, setAiError] = useState(null);
  const suggestReplyMutation = useMutation(
    () => api.post('/ai/suggest-reply', { conversationId: selectedId }).then((r) => r.data),
    {
      onMutate: () => {
        setAiPanelOpen(true);
        setAiResult(null);
        setAiError(null);
      },
      onSuccess: (data) => {
        if (!data?.drafts || data.drafts.length === 0) {
          setAiError('AI returned no drafts');
          return;
        }
        setAiResult(data);
      },
      onError: (err) => {
        setAiError(err.response?.data?.error || 'Could not generate drafts');
      },
    }
  );

  // Operator picked one of the 3 drafts → pour it into the composer
  // and close the panel.
  const useAiDraft = (text) => {
    setComposerBody(text);
    setAiPanelOpen(false);
    requestAnimationFrame(() => {
      const el = composerRef.current;
      if (el) {
        el.focus();
        const len = text.length;
        el.setSelectionRange(len, len);
      }
    });
  };

  // ☑ Suggest tasks — parallel feature to suggest-reply. Reads the same
  // conversation context, asks the OpenRouter `owl-alpha` model for
  // concrete follow-up tasks, opens a review panel. Operator picks
  // which ones to create; we POST them one-by-one to /api/tasks.
  const [tasksPanelOpen, setTasksPanelOpen] = useState(false);
  const [tasksResult, setTasksResult] = useState(null);
  const [tasksError, setTasksError] = useState(null);
  const suggestTasksMutation = useMutation(
    () => api.post('/ai/suggest-tasks', { conversationId: selectedId }).then((r) => r.data),
    {
      onMutate: () => {
        setTasksPanelOpen(true);
        setTasksResult(null);
        setTasksError(null);
      },
      onSuccess: (data) => { setTasksResult(data); },
      onError: (err) => {
        setTasksError(err.response?.data?.error || 'Could not generate tasks');
      },
    }
  );

  // Take the operator's picks and create them as real Tasks via the
  // existing tasks API. Each task is linked back to the conversation's
  // lead, dueDate is derived from the model's hour offset.
  const createSelectedTasks = async (picked) => {
    if (!picked?.length || !tasksResult?.leadId) return;
    const now = Date.now();
    const creations = picked.map((t) => api.post('/tasks', {
      title: t.title,
      description: t.description,
      type: t.type,
      priority: t.priority,
      leadId: tasksResult.leadId,
      dueDate: Number.isInteger(t.dueOffsetHours)
        ? new Date(now + t.dueOffsetHours * 3600_000).toISOString()
        : null,
    }));
    try {
      const results = await Promise.allSettled(creations);
      const ok = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.length - ok;
      if (ok) toast.success(`${ok} task${ok === 1 ? '' : 's'} created`);
      if (failed) toast.error(`${failed} task${failed === 1 ? '' : 's'} failed to save`);
      queryClient.invalidateQueries(['tasks']);
      setTasksPanelOpen(false);
    } catch (err) {
      toast.error('Could not save tasks');
    }
  };

  // (DM availability + platform derivations live further up so the
  // composerMode useEffect can depend on them without a TDZ.)

  // WhatsApp 24h customer-service window — server-computed from the last
  // inbound WhatsApp message. Re-keyed on the message count so a fresh
  // inbound reopens it without a manual refresh; a 60s tick keeps the
  // countdown honest while the composer sits open.
  const waActive = composerMode === 'whatsapp' && isWhatsappConfigured && !!selectedLeadId;
  const { data: waWindow } = useQuery(
    ['wa-window', selectedLeadId, messages.length],
    () => leadsAPI.getWhatsAppWindow(selectedLeadId).then((r) => r.data),
    { enabled: waActive, staleTime: 30 * 1000, retry: false }
  );
  const waWindowKnown = !!waWindow;
  const waWindowOpen = waWindow ? !!waWindow.open : true;
  const waTemplateMode = waActive && waWindowKnown && !waWindowOpen;
  const [, setWaTick] = useState(0);
  useEffect(() => {
    if (!waActive) return undefined;
    const t = setInterval(() => setWaTick((x) => x + 1), 60 * 1000);
    return () => clearInterval(t);
  }, [waActive]);
  const { data: waTemplatesData, isLoading: waTemplatesLoading } = useQuery(
    ['wa-templates'],
    () => api.get('/inbound-settings/whatsapp/templates').then((r) => r.data),
    { enabled: waTemplateMode, staleTime: 5 * 60 * 1000, retry: false }
  );
  const waTemplates = waTemplatesData?.templates || [];
  useEffect(() => {
    setWaPending([]);
    setWaTemplate(null);
    setWaParams([]);
    setWaHeaderParam('');
    setWaAttachMenuOpen(false);
  }, [selectedLeadId]);
  useEffect(() => { setWaPending([]); setWaAttachMenuOpen(false); }, [composerMode]);
  const attachEnabled = ATTACH_CHANNELS.has(composerMode) && !waTemplateMode;
  const pickWaTemplate = (id) => {
    const t = waTemplates.find((x) => x.id === id) || null;
    setWaTemplate(t);
    setWaParams(t ? Array.from({ length: t.paramCount }, () => '') : []);
    setWaHeaderParam('');
  };
  const waTemplatePreview = waTemplate
    ? waTemplate.bodyText.replace(/\{\{(\d+)\}\}/g, (_, n) => waParams[Number(n) - 1] || `{{${n}}}`)
    : '';
  const waHeaderNeedsParam = !!(waTemplate?.headerFormat === 'TEXT' && /\{\{1\}\}/.test(waTemplate.headerText || ''));
  const waTemplateReady = !!waTemplate && waParams.every((p) => String(p).trim()) && (!waHeaderNeedsParam || waHeaderParam.trim());

  const addWaFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const ok = [];
    for (const f of files) {
      if (f.size > WA_UPLOAD_MAX) { toast.error(`${f.name} is larger than 10 MB`); continue; }
      ok.push({ kind: 'file', file: f, name: f.name, size: f.size });
    }
    setWaPending((prev) => [...prev, ...ok].slice(0, 10));
    setWaAttachMenuOpen(false);
  };
  const addWaExisting = (att) => {
    setWaPending((prev) => prev.some((p) => p.kind === 'existing' && p.id === String(att._id))
      ? prev
      : [...prev, { kind: 'existing', id: String(att._id), name: att.originalName || att.filename, size: att.size }].slice(0, 10));
    setWaAttachMenuOpen(false);
  };

  // Standard 24h Messenger reply window. HUMAN_AGENT (7-day
  // extension) requires explicit Meta App Review approval — even on
  // the test-user / dev path. Until approved, sends past 24h are
  // rejected by Meta.
  const lastInboundAt = useMemo(() => {
    const inbound = messages
      .filter((m) => m.direction === 'inbound' && (m.source === 'messenger' || m.source === 'instagram'))
      .map((m) => new Date(m.createdAt).getTime());
    if (!inbound.length) return null;
    return new Date(Math.max(...inbound));
  }, [messages]);
  const messengerWindowOpen = lastInboundAt
    ? (Date.now() - lastInboundAt.getTime()) < 24 * 3600 * 1000
    : false;
  const messengerWindowRemaining = lastInboundAt
    ? Math.max(0, 24 * 3600 * 1000 - (Date.now() - lastInboundAt.getTime()))
    : 0;

  // First-come-first-served gate: if the lead is unassigned, we
  // confirm with the operator before sending — replying claims the
  // lead. Send-time check is the source of truth (server enforces
  // atomically); this dialog is just UX courtesy so the operator
  // knows what's about to happen.
  const [claimConfirm, setClaimConfirm] = useState({ open: false, run: null });
  const isLeadAdmin = currentUser?.role === 'admin' || currentUser?.role === 'superadmin';
  // assignedTo holds the ObjectId reference; assignedUser is the
  // populated {name,email} the GET /:id endpoint adds separately.
  const leadOwnerId = lead?.assignedTo?._id || lead?.assignedTo || null;
  const leadOwnerSelf = leadOwnerId && String(leadOwnerId) === String(currentUser?._id);
  const leadOwnerOther = leadOwnerId && !leadOwnerSelf;
  const leadUnassigned = !leadOwnerId;
  const leadOwnerName =
    lead?.assignedUser?.name ||
    lead?.assignedUser?.email ||
    lead?.assignedTo?.name ||
    lead?.assignedTo?.email ||
    null;
  const composerLocked = leadOwnerOther && !isLeadAdmin;

  const doSend = () => {
    const body = composerBody.trim();
    if (composerMode === 'whatsapp' && waTemplateMode) {
      sendWhatsappTemplateMutation.mutate();
      return;
    }
    if (!body && !(composerMode !== 'email' && waPending.length)) return;
    if (composerMode === 'email') {
      const subject = replySubject || 'Reply from Gainrhino CRM';
      sendEmailMutation.mutate({
        leadId: selectedLeadId,
        payload: { subject, message: body, emailType: 'custom', attachments: [], recipientEmail: lead.email, raw: true }
      });
    } else if (composerMode === 'messenger') {
      sendMessengerMutation.mutate({ leadId: selectedLeadId, payload: { message: body } });
    } else if (composerMode === 'whatsapp') {
      sendWhatsappMutation.mutate({ leadId: selectedLeadId, payload: { message: body, phoneField: 'phone' } });
    } else {
      sendSmsMutation.mutate({ leadId: selectedLeadId, payload: { message: body, phoneField: 'phone', topic: '' } });
    }
  };

  const handleSend = () => {
    if (!selectedLeadId) return;
    const body = composerBody.trim();
    const attachOnly = (composerMode !== 'email' && waPending.length > 0) || (composerMode === 'whatsapp' && waTemplateMode);
    if (!body && !attachOnly) return toast.error('Message is required');

    // Channel-specific guards still run first so we don't claim a
    // lead just to fail on missing email / SMS config moments later.
    if (composerMode === 'email') {
      if (!isSmtpConfigured) { setSettingsRequiredModal({ isOpen: true, type: 'email' }); return; }
      if (!lead?.email) return toast.error('Lead has no email address');
    } else if (composerMode === 'messenger') {
      if (!dmAvailable) return toast.error('No Messenger / Instagram conversation available for this lead');
      if (messengerTag && !messengerWindowOpen) {
        return toast.error('Outside the 24-hour Messenger reply window — Meta will reject this message');
      }
    } else if (composerMode === 'whatsapp') {
      if (!isWhatsappConfigured) return toast.error('WhatsApp is not configured — connect it under Inbound Settings');
      if (!lead?.phone) return toast.error('Lead has no phone number');
      if (waTemplateMode && !waTemplateReady) return toast.error('Pick a template and fill in every placeholder');
    } else {
      if (!isSmsConfigured) { setSettingsRequiredModal({ isOpen: true, type: 'sms' }); return; }
      if (!lead?.phone) return toast.error('Lead has no phone number');
    }

    // Non-admin trying to reply to a lead owned by someone else —
    // composer is already disabled, but belt-and-suspenders here.
    if (composerLocked) return toast.error(`Lead is assigned to ${leadOwnerName || 'another operator'}`);

    // Unassigned → confirm the implicit claim before firing the send.
    if (leadUnassigned) {
      setClaimConfirm({ open: true, run: doSend });
      return;
    }
    doSend();
  };

  const closeImagePreview = React.useCallback(() => setImagePreview(null), []);

  return (
    <div className="flex h-[calc(100vh-64px)] bg-gray-50">
      {imagePreview && (
        <ImageLightbox leadId={imagePreview.leadId} att={imagePreview.att} onClose={closeImagePreview} />
      )}
      <SettingsRequiredModal
        isOpen={settingsRequiredModal.isOpen}
        onClose={() => setSettingsRequiredModal((prev) => ({ ...prev, isOpen: false }))}
        type={settingsRequiredModal.type}
        canManageSettings={canManageSettings}
      />
      <ConfirmationModal
        isOpen={claimConfirm.open}
        onClose={() => setClaimConfirm({ open: false, run: null })}
        onConfirm={() => {
          const fn = claimConfirm.run;
          setClaimConfirm({ open: false, run: null });
          if (fn) fn();
        }}
        title="Claim this lead?"
        message={`Replying will assign this lead to you. Other operators will only be able to view it; admins can reassign if needed. Continue?`}
        confirmText="Yes, claim and send"
      />
      <ConfirmationModal {...phoneClaim.confirmProps} />
      {/* Override embedded utility classes inside outbound bubbles so blue
          background stays readable regardless of the inline HTML colors. */}
      <style>{`
        /* Keep the bubble's blue background; force every child's text to white
           and its own backgrounds/colors out of the way. */
        .chat-bubble-out * {
          color: #ffffff !important;
          background-color: transparent !important;
          background: transparent !important;
          border-color: rgba(255,255,255,0.25) !important;
        }
        .chat-bubble-out a {
          text-decoration: underline;
        }
      `}</style>
      {/* Left: conversation list. Below md the list and the chat are
          mutually exclusive — selecting a conversation hides the list and
          shows the chat with a back button. Above md they're side-by-side
          and the list keeps a fixed 360px column. */}
      <aside className={`${selectedId ? 'hidden md:flex' : 'flex'} w-full md:w-[360px] flex-shrink-0 border-r border-gray-200 bg-white flex-col`}>
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              Inbox
              {unreadCount > 0 && (
                <span className="px-2 py-0.5 text-xs font-semibold bg-blue-100 text-blue-700 rounded-full">
                  {unreadCount}
                </span>
              )}
            </h1>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
                title="Mark every conversation as read"
              >
                <CheckCheck size={14} />
                Mark all read
              </button>
            )}
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search conversations..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div className="flex flex-wrap gap-1.5 mt-3">
            {['all', 'email', 'sms', 'phone', 'webform', 'meta_ad', 'google_ad', 'tiktok_ad', 'messenger', 'instagram', 'whatsapp', 'appointment'].map((k) => {
              const meta = CHANNEL_META[k];
              const active = channelFilter === k;
              return (
                <button
                  key={k}
                  onClick={() => setChannelFilter(k)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${active ? `${meta.color} ring-2 ${meta.ring}` : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`}
                >
                  {meta.label}
                </button>
              );
            })}
          </div>
          <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
            <ArrowUpDown size={12} />
            <select
              value={sortBy}
              onChange={(e) => persistSort(e.target.value)}
              className="bg-transparent border border-gray-200 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="newest">Newest first</option>
              <option value="unread">Unread first</option>
              <option value="oldest">Oldest first</option>
            </select>
            <span className="ml-auto text-[10px]" title="j/k or ↑/↓ to navigate, ⌘+Enter to send">⌨ j/k to nav</span>
          </div>
        </div>

        <div
          className="flex-1 overflow-y-auto"
          onScroll={(e) => {
            const el = e.currentTarget;
            if (el.scrollHeight - el.scrollTop - el.clientHeight < 200
                && hasMoreConversations && !isLoadingMoreConversations) {
              fetchMoreConversations();
            }
          }}
        >
          {isLoading && <div className="p-4 text-sm text-gray-400">Loading...</div>}
          {!isLoading && sortedConversations.length === 0 && (
            <div className="p-6 text-center text-sm text-gray-400">No conversations match.</div>
          )}
          {sortedConversations.map((c) => {
            const isActive = c._id === selectedId;
            const name = `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.email || c.phone || '(unnamed)';
            const unread = isUnread(c);
            const isPinned = !!c.pinned;
            return (
              <div
                key={c._id}
                className={`group relative w-full border-b border-gray-100 flex gap-3 items-start hover:bg-gray-50 transition-colors ${isActive ? 'bg-primary-50 border-l-4 border-l-primary-500' : ''}`}
              >
                <button
                  onClick={() => setSelectedId(c._id)}
                  className="flex-1 text-left px-4 py-3 flex gap-3 items-start min-w-0"
                >
                  <LeadAvatar lead={c} size={40} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className={`truncate ${unread ? 'font-bold text-gray-900' : 'font-semibold text-gray-900'}`}>
                        {name}
                      </span>
                      <span className="text-[10px] text-gray-400 flex-shrink-0">{relativeTime(c.lastMessageAt)}</span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                      {/* Primary badge = the most-recent-activity channel.
                          Secondary badges = the other channels this lead
                          has had conversations on. Same lead with both an
                          SMS thread and a phone call thread shows two
                          badges side-by-side. */}
                      {c.noConversation ? (
                        <SourceBadge source={c.source} small />
                      ) : (
                        <>
                          <ChannelBadge source={c.channel || 'web'} small />
                          {(c.channelBadges || []).slice(1).map((ch) => (
                            <ChannelBadge key={ch} source={ch} small />
                          ))}
                        </>
                      )}
                      {unread && (
                        <span className="inline-block w-2 h-2 rounded-full bg-blue-500" title="Unread" />
                      )}
                      {isPinned && <Pin size={10} className="text-amber-500" />}
                    </div>
                    <p className={`text-xs mt-1 line-clamp-2 ${unread ? 'text-gray-700 font-medium' : 'text-gray-500'}`}>
                      {c.noConversation ? (
                        <span className="italic text-gray-400">No messages yet</span>
                      ) : c.channel === 'phone' ? (
                        <span className="inline-flex items-center gap-1">
                          <Phone size={11} className="text-green-600" />
                          {(c.lastMessage?.direction === 'inbound' ? '← ' : '→ ') + stripHtml(c.lastMessage?.content)}
                        </span>
                      ) : (
                        stripHtml(c.lastMessage?.content)
                      )}
                    </p>
                  </div>
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); togglePin(c); }}
                  className={`absolute top-2 right-2 p-1 rounded transition-opacity ${
                    isPinned ? 'text-amber-500 opacity-100' : 'text-gray-300 opacity-0 group-hover:opacity-100 hover:text-gray-600'
                  }`}
                  title={isPinned ? 'Unpin' : 'Pin to top'}
                >
                  {isPinned ? <PinOff size={12} /> : <Pin size={12} />}
                </button>
              </div>
            );
          })}
          {isLoadingMoreConversations && (
            <div className="p-3 text-center text-xs text-gray-400">Loading more…</div>
          )}
          {!isLoadingMoreConversations && hasMoreConversations && sortedConversations.length > 0 && (
            <button
              onClick={() => fetchMoreConversations()}
              className="block w-full p-3 text-center text-xs text-gray-500 hover:text-gray-800 hover:bg-gray-50"
            >
              Load more conversations
            </button>
          )}
        </div>
      </aside>

      {/* Right: chat. Hidden on mobile when no conversation is selected
          (the list takes the whole width instead). */}
      <section className={`${selectedId ? 'flex' : 'hidden md:flex'} flex-1 flex-col bg-gray-50`}>
        {!selectedId ? (
          <div className="flex-1 flex items-center justify-center text-gray-400">Select a conversation</div>
        ) : !lead ? (
          <div className="flex-1 flex items-center justify-center text-gray-400">Loading...</div>
        ) : (
          <>
            {/* Chat header */}
            <header className="px-4 md:px-6 py-3 border-b border-gray-200 bg-white flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => setSelectedId(null)}
                className="md:hidden p-2 -ml-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                title="Back to inbox"
              >
                <ArrowLeft size={18} />
              </button>
              <Link
                to={`/leads/${lead._id}`}
                className="flex items-center gap-3 group min-w-0 flex-1"
                title="Open lead profile"
              >
                <LeadAvatar lead={lead} size={40} />
                <div className="min-w-0">
                  <div className="font-semibold text-gray-900 group-hover:text-primary-600 transition-colors truncate">
                    {`${lead.firstName || ''} ${lead.lastName || ''}`.trim() || '(unnamed)'}
                  </div>
                  <div className="text-xs text-gray-500 flex items-center gap-2 truncate">
                    {lead.email && <span className="truncate">{lead.email}</span>}
                    {lead.phone && <span>· {lead.phone}</span>}
                  </div>
                </div>
              </Link>
              <div className="flex items-center gap-2 flex-shrink-0">
                {lead.phone && (
                  <button
                    onClick={() => {
                      // Claim-first: if the lead is unassigned, clicking
                      // the call button takes ownership before we hand
                      // the dial off to the system phone app. Same flow
                      // the phone number link in Lead Detail uses, so
                      // the operator sees the same "this will be assigned
                      // to you" prompt regardless of where they dial from.
                      if (phoneClaim.ownerSelf) {
                        window.location.href = `tel:${lead.phone}`;
                        return;
                      }
                      phoneClaim.requestClaim(() => { window.location.href = `tel:${lead.phone}`; });
                    }}
                    disabled={phoneClaim.locked}
                    className={`p-2 rounded-lg transition-colors flex items-center gap-1.5 ${
                      phoneClaim.locked
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'text-green-700 bg-green-50 hover:bg-green-100'
                    }`}
                    title={phoneClaim.locked ? 'Locked' : `Call ${lead.phone}`}
                  >
                    <Phone size={16} />
                    <span className="hidden sm:inline text-xs font-medium">Call</span>
                  </button>
                )}
                {lead.phone && (
                  <BrowserCallButton
                    lead={lead}
                    phone={lead.phone}
                    size={16}
                    className="p-2 rounded-lg"
                    disabled={phoneClaim.locked}
                    guard={(go) => (phoneClaim.ownerSelf ? go() : phoneClaim.requestClaim(go))}
                  />
                )}
                {showThreadSearch ? (
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                    <input
                      autoFocus
                      value={threadSearch}
                      onChange={(e) => setThreadSearch(e.target.value)}
                      placeholder="Search this thread..."
                      className="pl-8 pr-7 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 w-56"
                      onKeyDown={(e) => { if (e.key === 'Escape') { setThreadSearch(''); setShowThreadSearch(false); } }}
                    />
                    <button
                      onClick={() => { setThreadSearch(''); setShowThreadSearch(false); }}
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-700"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowThreadSearch(true)}
                    className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                    title="Search this conversation"
                  >
                    <Search size={16} />
                  </button>
                )}
              </div>
            </header>

            {/* Messages */}
            <div
              ref={chatScrollRef}
              onScroll={handleChatScroll}
              className="flex-1 overflow-y-auto p-6 space-y-4"
            >
              {isLoadingOlder && (
                <p className="text-center text-gray-400 text-xs">Loading older messages…</p>
              )}
              {!isLoadingOlder && hasOlderMessages && messages.length > 0 && (
                <button
                  onClick={() => {
                    if (chatScrollRef.current) prevScrollHeightRef.current = chatScrollRef.current.scrollHeight;
                    fetchOlderMessages();
                  }}
                  className="block mx-auto text-xs text-gray-500 hover:text-gray-800 underline"
                >
                  Load older messages
                </button>
              )}
              {messages.length === 0 && <p className="text-center text-gray-400 text-sm">No messages yet.</p>}
              {messages.length > 0 && visibleMessages.length === 0 && (
                <p className="text-center text-gray-400 text-sm">No messages match "{threadSearch}".</p>
              )}
              {visibleMessages.map((m, i) => {
                const isOutbound = m.direction !== 'inbound';
                const meta = CHANNEL_META[m.source] || CHANNEL_META.web;
                const { Icon } = meta;

                // Phone calls aren't chat — they're events with metadata
                // (duration, disposition, recording). Render a dedicated
                // call card instead of the chat bubble so the operator
                // can see at a glance whether the call connected and
                // tap play if a recording exists. Matches the green
                // visual language used on the Lead Detail call note.
                if (m.source === 'phone') {
                  const cf = m.customFields instanceof Map
                    ? Object.fromEntries(m.customFields)
                    : (m.customFields || {});
                  const durSec = Number(cf.duration_seconds || 0);
                  const durText = durSec > 0
                    ? (durSec >= 60 ? `${Math.floor(durSec / 60)}m ${durSec % 60}s` : `${durSec}s`)
                    : '—';
                  const disposition = cf.disposition_label || (durSec > 0 ? 'Answered' : 'Missed');
                  const isMissed = /miss/i.test(disposition);
                  // NetSapiens CDRs carry orig_*; VoIP.ms CDRs carry
                  // to_number / from_number. Fall through both shapes.
                  const otherParty = isOutbound
                    ? (cf.orig_to_user || cf.to_number || '')
                    : (cf.orig_from_name || cf.orig_from_user || cf.from_number || '');
                  return (
                    <div key={m._id || i} className={`flex ${isOutbound ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[75%] ${isOutbound ? 'order-2' : 'order-1'}`}>
                        <div className="flex items-center gap-2 mb-1 text-[11px] text-gray-500">
                          <Phone size={12} />
                          <span>{isOutbound ? 'Outbound call' : 'Inbound call'}</span>
                          <span>·</span>
                          <span>{new Date(m.createdAt).toLocaleString()}</span>
                        </div>
                        <div className={`rounded-2xl border shadow-sm overflow-hidden ${isMissed ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                          <div className="flex items-center gap-3 px-4 py-3">
                            <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${isMissed ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-700'}`}>
                              <Phone size={18} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-semibold text-gray-900">
                                  {isOutbound ? '→' : '←'} {otherParty || 'Unknown'}
                                </span>
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${
                                  isMissed ? 'bg-red-200 text-red-800'
                                  : /voicemail/i.test(disposition) ? 'bg-amber-200 text-amber-800'
                                  : 'bg-green-200 text-green-800'
                                }`}>
                                  {disposition}
                                </span>
                              </div>
                              <div className="text-xs text-gray-600 mt-0.5">Duration: {durText}</div>
                            </div>
                          </div>
                          {cf.has_recording && (
                            <div className="px-4 pb-3 pt-1 border-t border-green-200/60 bg-white/40">
                              <VoipRecordingPlayer kind="message" id={m._id} className="h-8" />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                }

                return (
                  <div key={m._id || i} className={`flex ${isOutbound ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[70%] ${isOutbound ? 'order-2' : 'order-1'}`}>
                      <div className="flex items-center gap-2 mb-1 text-[11px] text-gray-500">
                        <Icon size={12} />
                        <span>{meta.label}</span>
                        <span>·</span>
                        <span>{new Date(m.createdAt).toLocaleString()}</span>
                        {isOutbound && m.source === 'whatsapp' && m.status && (
                          <WaStatusTicks status={m.status} failureReason={m.failureReason} />
                        )}
                        {isOutbound && isAiAutoReply(m) && (
                          <button
                            type="button"
                            onClick={() => setOpenAiAuditId((cur) => (cur === (m._id || i) ? null : (m._id || i)))}
                            aria-expanded={openAiAuditId === (m._id || i)}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 font-semibold hover:bg-violet-200"
                            title="Sent automatically by AI Auto-Reply — show sources and checks"
                          >
                            <Sparkles size={10} /> AI
                          </button>
                        )}
                      </div>
                      {FORM_NOTE_SOURCES.has(m.source) || looksLikeFormSubmission(m.content) ? (
                        <MetaLeadFormCard
                          content={m.content || ''}
                          leadgenId={leadgenIdFromTags}
                          onPhoneClick={(phone, e) => {
                            if (phoneClaim.ownerSelf) return;
                            e.preventDefault();
                            phoneClaim.requestClaim(() => { window.location.href = `tel:${phone}`; });
                          }}
                          onEmailClick={(email, e) => {
                            if (phoneClaim.ownerSelf) return;
                            e.preventDefault();
                            phoneClaim.requestClaim(() => { window.location.href = `mailto:${email}`; });
                          }}
                        />
                      ) : (
                        <div
                          className={`rich-html rounded-2xl px-4 py-2.5 text-sm leading-relaxed shadow-sm ${isOutbound ? 'bg-primary-600 text-white rounded-br-sm chat-bubble-out' : 'bg-white text-gray-800 border border-gray-200 rounded-bl-sm'}`}
                          dangerouslySetInnerHTML={{
                            // Allowlist tuned for both chat-style notes and full
                            // HTML email bodies (table layouts, embedded images,
                            // blockquoted replies). Script / iframe / object
                            // remain blocked by DOMPurify defaults.
                            __html: DOMPurify.sanitize(m.content || '', {
                              ALLOWED_TAGS: [
                                'div','span','h1','h2','h3','h4','p','br','hr',
                                'a','b','i','u','strong','em','small','sub','sup',
                                'ul','ol','li','blockquote','pre','code',
                                'table','thead','tbody','tfoot','tr','td','th',
                                'img','figure','figcaption',
                              ],
                              ALLOWED_ATTR: [
                                'href','target','rel','class','style',
                                'src','alt','title','width','height',
                                'colspan','rowspan','align',
                              ],
                            })
                          }}
                        />
                      )}
                      {Array.isArray(m.attachments) && m.attachments.length > 0 && (
                        <div className={`mt-1.5 flex flex-wrap gap-2 ${isOutbound ? 'justify-end' : ''}`}>
                          {m.attachments.map((att, i) => (
                            <MessageAttachment
                              key={att.attachmentId || `${att.url}-${i}`}
                              leadId={m.leadId || selectedLeadId}
                              att={att}
                              isOutbound={isOutbound}
                              onPreview={setImagePreview}
                            />
                          ))}
                        </div>
                      )}
                      {isOutbound && isAiAutoReply(m) && openAiAuditId === (m._id || i) && (
                        <AiReplyAudit m={m} />
                      )}
                      {isOutbound && m.source === 'whatsapp' && m.status === 'failed' && (
                        <div className="mt-1 text-[11px] text-red-600">Not delivered{m.failureReason ? `: ${m.failureReason}` : ''}</div>
                      )}
                      {/* Phone Agent recording — playable inline under the
                          transcript bubble, streamed via our proxy. */}
                      {m.source === 'phone_agent' && (() => {
                        const cf = m.customFields instanceof Map
                          ? Object.fromEntries(m.customFields)
                          : (m.customFields || {});
                        if (!cf.has_recording) return null;
                        return (
                          <div className="mt-1.5">
                            <VoipRecordingPlayer srcPath={`phone-agent/recording/message/${m._id}`} className="h-8" />
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                );
              })}
              <div ref={chatEndRef} />
            </div>

            {/* Composer */}
            <footer className="border-t border-gray-200 bg-white p-4 relative">
              <AIReplyPanel
                isOpen={aiPanelOpen}
                isLoading={suggestReplyMutation.isLoading}
                result={aiResult}
                error={aiError}
                onPick={useAiDraft}
                onRegenerate={() => suggestReplyMutation.mutate()}
                onClose={() => setAiPanelOpen(false)}
              />
              <AISuggestTasksPanel
                isOpen={tasksPanelOpen}
                isLoading={suggestTasksMutation.isLoading}
                result={tasksResult}
                error={tasksError}
                onCreate={createSelectedTasks}
                onClose={() => setTasksPanelOpen(false)}
              />
              {/* Ownership status strip — first-come-first-served context
                  before the operator types. Open leads call out that
                  replying will claim them; locked leads disable the
                  composer for non-admins. */}
              {selectedLeadId && lead && (
                <div className={`mb-2 px-2.5 py-1.5 rounded-md text-[11px] font-medium flex items-center justify-between gap-2 ${
                  composerLocked ? 'bg-red-50 text-red-800 border border-red-100'
                  : leadUnassigned ? 'bg-amber-50 text-amber-800 border border-amber-100'
                  : leadOwnerSelf ? 'bg-emerald-50 text-emerald-800 border border-emerald-100'
                  : 'bg-blue-50 text-blue-800 border border-blue-100'
                }`}>
                  <span>
                    {composerLocked && <>🔒 Locked — assigned to <span className="font-semibold">{leadOwnerName}</span>. Ask an admin to reassign.</>}
                    {!composerLocked && leadUnassigned && <>🔓 Open lead — sending a reply will claim it for you.</>}
                    {!composerLocked && leadOwnerSelf && <>✓ Assigned to you.</>}
                    {!composerLocked && leadOwnerOther && isLeadAdmin && <>🛡 Admin override — assigned to <span className="font-semibold">{leadOwnerName}</span>, you can still reply.</>}
                  </span>
                  {leadUnassigned && (
                    <button
                      type="button"
                      onClick={() => phoneClaim.requestClaim()}
                      className="px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-600 hover:bg-amber-700 text-white"
                    >
                      Take it
                    </button>
                  )}
                </div>
              )}
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="text-xs text-gray-500">Reply via:</span>
                <button
                  onClick={() => setComposerMode('sms')}
                  disabled={!lead.phone || !!smsOptOutNotice(lead)}
                  title={smsOptOutNotice(lead) || undefined}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${composerMode === 'sms' ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'} disabled:opacity-50`}
                >
                  <MessageSquare size={12} className="inline mr-1" /> SMS
                </button>
                <button
                  onClick={() => setComposerMode('email')}
                  disabled={!lead.email}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${composerMode === 'email' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'} disabled:opacity-50`}
                >
                  <Mail size={12} className="inline mr-1" /> Email
                </button>
                {isWhatsappConfigured && (
                  <button
                    onClick={() => setComposerMode('whatsapp')}
                    disabled={!lead.phone}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${composerMode === 'whatsapp' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'} disabled:opacity-50`}
                  >
                    <MessageCircle size={12} className="inline mr-1" /> WhatsApp
                  </button>
                )}
                {dmAvailable && (
                  <button
                    onClick={() => setComposerMode('messenger')}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${composerMode === 'messenger' ? (isInstagramThread ? 'bg-pink-600 text-white' : 'bg-blue-600 text-white') : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  >
                    {isInstagramThread
                      ? <><Instagram size={12} className="inline mr-1" /> Instagram</>
                      : <><Facebook size={12} className="inline mr-1" /> Messenger</>}
                  </button>
                )}
                {composerMode === 'whatsapp' && waWindowKnown && (
                  <span className={`inline-flex items-center gap-1 text-[11px] ${waWindowOpen ? 'text-gray-500' : 'text-amber-700 font-medium'}`}>
                    <Clock size={11} />
                    {waWindowOpen
                      ? `WhatsApp reply window: ${formatRemaining(waWindow.expiresAt)} left`
                      : (waWindow.lastInboundAt ? '24h window closed — only templates can be sent' : 'No customer message yet — start with a template')}
                  </span>
                )}
                {composerMode === 'messenger' && messengerTag && (
                  <span className={`text-[11px] ${messengerWindowOpen ? 'text-gray-500' : 'text-red-600 font-medium'}`}>
                    {messengerWindowOpen
                      ? `${messengerLabel} reply window: ${Math.floor(messengerWindowRemaining / 3600000)}h ${Math.floor((messengerWindowRemaining % 3600000) / 60000)}m left`
                      : `${messengerLabel} 24h window expired — Meta will reject this`}
                  </span>
                )}
                {composerMode === 'messenger' && !messengerTag && hasLeadgenTag && (
                  <span className="text-[11px] text-amber-700">
                    {messengerLabel} thread will be looked up by ad id on send.
                  </span>
                )}
              </div>
              {composerMode === 'email' && replySubject && (
                <div className="mb-2 px-3 py-1.5 bg-purple-50 border border-purple-100 rounded-lg text-xs text-purple-900">
                  <span className="font-semibold mr-1">Subject:</span>
                  <span>{replySubject}</span>
                  <span className="text-purple-500 ml-2">(auto-filled — replying to thread)</span>
                </div>
              )}
              {attachEnabled && waPending.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {waPending.map((p, i) => (
                    <span key={`${p.kind}-${p.id || p.name}-${i}`} className={`inline-flex items-center gap-1.5 text-xs rounded-full pl-2.5 pr-1 py-1 max-w-[240px] border ${composerMode === 'whatsapp' ? 'bg-green-50 border-green-200 text-green-900' : 'bg-gray-50 border-gray-200 text-gray-800'}`}>
                      <Paperclip size={11} className="shrink-0" />
                      <span className="truncate">{p.name}</span>
                      {p.size ? <span className="text-green-700/70 shrink-0">{formatBytes(p.size)}</span> : null}
                      <button type="button" onClick={() => setWaPending((prev) => prev.filter((_, j) => j !== i))} className="p-0.5 rounded-full hover:bg-black/5" title="Remove">
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              {waTemplateMode && (
                <div className="mb-2 border border-amber-200 bg-amber-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-xs text-amber-900 font-medium mb-2">
                    <LayoutTemplate size={13} />
                    Send a WhatsApp template
                  </div>
                  {waTemplatesLoading ? (
                    <div className="text-xs text-amber-800">Loading templates…</div>
                  ) : waTemplates.length === 0 ? (
                    <div className="text-xs text-amber-800">
                      No approved templates on your WhatsApp Business Account yet. Create one in{' '}
                      <a href="https://business.facebook.com/wa/manage/message-templates/" target="_blank" rel="noreferrer" className="underline font-medium">WhatsApp Manager → Message templates</a>
                      {' '}and it will appear here once Meta approves it.
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2">
                      <select
                        value={waTemplate?.id || ''}
                        onChange={(e) => pickWaTemplate(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-amber-200 bg-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400"
                      >
                        <option value="">Choose a template…</option>
                        {waTemplates.map((t) => (
                          <option key={t.id} value={t.id}>{t.name} · {t.language} · {t.category.toLowerCase()}</option>
                        ))}
                      </select>
                      {waTemplate && (
                        <>
                          {waHeaderNeedsParam && (
                            <input
                              value={waHeaderParam}
                              onChange={(e) => setWaHeaderParam(e.target.value)}
                              placeholder="Header {{1}}"
                              className="w-full px-3 py-1.5 text-sm border border-amber-200 bg-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400"
                            />
                          )}
                          {waParams.length > 0 && (
                            <div className="grid sm:grid-cols-2 gap-2">
                              {waParams.map((v, i) => (
                                <input
                                  key={i}
                                  value={v}
                                  onChange={(e) => setWaParams((prev) => prev.map((x, j) => (j === i ? e.target.value : x)))}
                                  placeholder={`{{${i + 1}}}${i === 0 && lead?.firstName ? ` e.g. ${lead.firstName}` : ''}`}
                                  className="w-full px-3 py-1.5 text-sm border border-amber-200 bg-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400"
                                />
                              ))}
                            </div>
                          )}
                          <div className="bg-white border border-amber-100 rounded-lg px-3 py-2 text-sm text-gray-800 whitespace-pre-wrap">
                            {waHeaderNeedsParam && waHeaderParam ? <div className="font-semibold mb-1">{(waTemplate.headerText || '').replace('{{1}}', waHeaderParam)}</div> : null}
                            {waTemplatePreview}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
              <div className="flex items-end gap-2 relative">
                {attachEnabled && waAttachMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setWaAttachMenuOpen(false)} />
                    <div className="absolute bottom-full right-0 sm:right-24 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 w-[min(280px,calc(100vw-2rem))] text-sm">
                      <button
                        type="button"
                        onClick={() => waFileInputRef.current?.click()}
                        className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2 border-b border-gray-100"
                      >
                        <Paperclip size={14} className="text-gray-500" /> Upload from computer
                      </button>
                      <div className="px-3 pt-2 pb-1 text-[11px] uppercase tracking-wide text-gray-400">Lead files</div>
                      <div className="max-h-[180px] overflow-y-auto pb-1">
                        {(lead?.attachments || []).length === 0 ? (
                          <div className="px-3 py-2 text-xs text-gray-500">No files on this lead yet.</div>
                        ) : (lead.attachments.map((att) => (
                          <button
                            key={att._id}
                            type="button"
                            onClick={() => addWaExisting(att)}
                            className="w-full text-left px-3 py-1.5 hover:bg-gray-50 flex items-center gap-2"
                          >
                            <FileText size={13} className="text-gray-400 shrink-0" />
                            <span className="truncate flex-1">{att.originalName || att.filename}</span>
                            <span className="text-[10px] text-gray-400 shrink-0">{formatBytes(att.size)}</span>
                          </button>
                        )))}
                      </div>
                    </div>
                  </>
                )}
                <input
                  ref={waFileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => { addWaFiles(e.target.files); e.target.value = ''; }}
                />
                {/* Emoji picker — anchored above the composer. Click-out
                    closes via the overlay below. */}
                {emojiOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setEmojiOpen(false)}
                    />
                    <div className="absolute bottom-full right-12 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg p-2 z-50 w-[280px]">
                      <div className="grid grid-cols-8 gap-1 max-h-[200px] overflow-y-auto">
                        {EMOJI_SET.map((e, i) => (
                          <button
                            key={`${e}-${i}`}
                            onClick={() => { insertAtCursor(e); }}
                            className="p-1.5 hover:bg-gray-100 rounded text-lg leading-none"
                          >
                            {e}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
                {waTemplateMode ? (
                  <div className="flex-1 px-3 py-2 text-xs text-gray-500 border border-dashed border-gray-200 rounded-lg bg-gray-50">
                    Free-form replies are locked until the customer writes back. Send a template above.
                  </div>
                ) : (
                <textarea
                  ref={composerRef}
                  value={composerBody}
                  onChange={(e) => setComposerBody(e.target.value)}
                  onKeyDown={(e) => {
                    // Cmd/Ctrl + Enter sends; plain Enter inserts a newline
                    // (don't surprise operators mid-paragraph). Pipedrive
                    // and Slack use the same modifier.
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  placeholder={
                    composerMode === 'email' ? 'Write your email...'
                      : composerMode === 'messenger' ? `Reply via ${messengerLabel}... (⌘+Enter to send)`
                      : composerMode === 'whatsapp' ? 'Reply via WhatsApp... (⌘+Enter to send)'
                      : 'Type a message... (⌘+Enter to send)'
                  }
                  rows={composerMode === 'email' ? 4 : 2}
                  className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                />
                )}
                {attachEnabled && (
                  <button
                    type="button"
                    onClick={() => setWaAttachMenuOpen((v) => !v)}
                    className={`inline-flex items-center justify-center w-10 h-10 rounded-lg hover:bg-gray-100 ${waPending.length ? 'text-green-600' : 'text-gray-500 hover:text-gray-700'}`}
                    title={
                      composerMode === 'sms' ? 'Attach a photo (sent as MMS)'
                        : composerMode === 'email' ? 'Attach files to this email'
                        : composerMode === 'messenger' ? 'Attach a photo or file'
                        : 'Attach a file (image, PDF, video, voice note)'
                    }
                  >
                    <Paperclip size={18} />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => suggestReplyMutation.mutate()}
                  disabled={suggestReplyMutation.isLoading || !selectedId}
                  className="inline-flex items-center justify-center w-10 h-10 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 disabled:opacity-50 rounded-lg"
                  title="Suggest reply with AI"
                >
                  {suggestReplyMutation.isLoading
                    ? <Loader2 size={18} className="animate-spin" />
                    : <Sparkles size={18} />}
                </button>
                {/* ☑ Suggest follow-up tasks. Same conversation context,
                    different model — owl-alpha specialises in short
                    structured extraction so action items come back
                    cleanly typed (call / email / meeting / etc.). */}
                <button
                  type="button"
                  onClick={() => suggestTasksMutation.mutate()}
                  disabled={suggestTasksMutation.isLoading || !selectedId}
                  className="inline-flex items-center justify-center w-10 h-10 text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50 disabled:opacity-50 rounded-lg"
                  title="Suggest follow-up tasks with AI"
                >
                  {suggestTasksMutation.isLoading
                    ? <Loader2 size={18} className="animate-spin" />
                    : <ClipboardList size={18} />}
                </button>
                <button
                  type="button"
                  onClick={() => setEmojiOpen((v) => !v)}
                  className="inline-flex items-center justify-center w-10 h-10 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                  title="Insert emoji"
                >
                  <Smile size={18} />
                </button>
                <button
                  onClick={handleSend}
                  disabled={
                    sendEmailMutation.isLoading
                    || sendSmsMutation.isLoading
                    || sendMessengerMutation.isLoading
                    || sendWhatsappMutation.isLoading
                    || sendWhatsappTemplateMutation.isLoading
                    || (waTemplateMode && !waTemplateReady)
                    // Only enforce the 24h window when we already
                    // have an active thread (messengerTag). Lead Ad
                    // leads without one can still attempt a first
                    // send — server discovers the PSID lazily.
                    || (composerMode === 'messenger' && messengerTag && !messengerWindowOpen)
                    // They texted STOP: no SMS until they text START.
                    || (composerMode === 'sms' && !!smsOptOutNotice(lead))
                    // First-come-first-served lock — non-admin can't
                    // send into someone else's lead.
                    || composerLocked
                  }
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium"
                >
                  {sendWhatsappMutation.isLoading || sendWhatsappTemplateMutation.isLoading
                    ? <Loader2 size={14} className="animate-spin" />
                    : <Send size={14} />}
                  {waTemplateMode ? 'Send template' : ((sendWhatsappMutation.isLoading || sendSmsMutation.isLoading || sendMessengerMutation.isLoading || sendEmailMutation.isLoading) && waPending.length ? 'Uploading…' : 'Send')}
                </button>
              </div>
              {composerMode === 'sms' && smsOptOutNotice(lead) && (
                <div className="mt-1.5 text-[11px] text-amber-700">{smsOptOutNotice(lead)}</div>
              )}
              {/* SMS length counter — operators care about cost (each
                  text = 1 send); a long text goes out as numbered parts. */}
              {composerMode === 'sms' && composerBody.length > 0 && (() => {
                const info = smsSegmentInfo(composerBody);
                return (
                  <div className="mt-1.5 text-[11px] text-gray-500 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span className={info.parts > 1 ? 'text-amber-600 font-medium' : ''}>
                      {info.len} chars · {info.parts > 1 ? `goes out as ${info.parts} numbered texts` : '1 text'}
                    </span>
                    {info.parts <= 1 && (
                      <>
                        <span className="text-gray-400">·</span>
                        <span>{SMS_SEGMENT_LIMIT - info.len} left</span>
                      </>
                    )}
                    {info.isUnicode && <span className="text-amber-600">· Unicode: carriers may bill more segments</span>}
                  </div>
                );
              })()}
            </footer>
          </>
        )}
      </section>
    </div>
  );
};

export default Inbox;
